import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target, TrendingUp, ChevronRight } from "lucide-react";

const verdictLabel = (p) => {
    if (!p?.verdict) return "—";
    if (p.verdict === "HOME") return p.home_abbr || p.home_team;
    if (p.verdict === "AWAY") return p.away_abbr || p.away_team;
    return "SARE";
};

const formatKickoff = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("sw-TZ", {
            timeZone: "Africa/Dar_es_Salaam",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
};

/**
 * Homepage auto-generated Top-3 Daily Picks card.
 * Consumes GET /api/daily-picks which is refreshed every 6h by the backend scheduler.
 */
const DailyPicksCard = () => {
    const [picks, setPicks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        axios
            .get(`${API}/daily-picks`)
            .then((r) => {
                if (alive) setPicks(r.data?.picks || []);
            })
            .catch(() => {})
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, []);

    if (!loading && picks.length === 0) return null;

    return (
        <Card className="bg-gradient-to-br from-electric/15 to-paper border-electric/40 overflow-hidden" data-testid="daily-picks-card">
            {/* Header */}
            <div className="bg-electric px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-white" />
                    <h3 className="font-heading font-black text-sm text-white uppercase tracking-widest">
                        Utabiri wa Leo
                    </h3>
                </div>
                <Badge className="bg-white/20 text-white border-0 text-[10px] uppercase tracking-wider">Top 3</Badge>
            </div>

            <div className="p-3 space-y-2.5">
                {loading ? (
                    <div className="space-y-2">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="h-16 bg-subtle/60 rounded animate-pulse" />
                        ))}
                    </div>
                ) : (
                    picks.slice(0, 3).map((p, idx) => (
                        <Link
                            key={p.id}
                            to="/utabiri"
                            className="block bg-subtle hover:bg-subtle/80 rounded p-3 transition-colors group"
                            data-testid={`daily-pick-${idx}`}
                        >
                            <div className="flex items-center justify-between text-[10px] text-white/50 uppercase tracking-wider mb-1.5">
                                <span className="flex items-center gap-1">
                                    {p.flag && <span>{p.flag}</span>}
                                    <span>{p.competition_name_sw || p.competition_name}</span>
                                </span>
                                <span>{formatKickoff(p.kickoff_iso)}</span>
                            </div>

                            <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="text-white text-sm font-medium truncate">
                                        {p.home_team} vs {p.away_team}
                                    </div>
                                    {p.top_reason_sw && (
                                        <div className="text-white/50 text-[11px] line-clamp-1 italic mt-0.5">
                                            "{p.top_reason_sw}"
                                        </div>
                                    )}
                                </div>

                                <div className="shrink-0 text-right">
                                    <div className="text-[10px] text-white/50 uppercase">Pick</div>
                                    <div className="text-electric font-heading font-black text-base leading-none">
                                        {verdictLabel(p)}
                                    </div>
                                </div>

                                <div className="shrink-0 flex flex-col items-center min-w-[48px]">
                                    <div className="text-electric font-mono font-bold text-lg leading-none">
                                        {p.confidence}%
                                    </div>
                                    <div className="text-white/40 text-[9px] mt-0.5">
                                        {p.sources_ok}/{p.sources_total}
                                    </div>
                                </div>

                                <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-electric transition-colors" />
                            </div>
                        </Link>
                    ))
                )}

                <Link
                    to="/utabiri"
                    className="block text-center text-electric text-xs font-bold uppercase tracking-wider py-2 hover:underline"
                    data-testid="daily-picks-cta"
                >
                    <TrendingUp className="h-3 w-3 inline-block mr-1" />
                    Chukua Utabiri Wako →
                </Link>
            </div>
        </Card>
    );
};

export default DailyPicksCard;
